import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { BRAND } from '@/utils/constants';
import logoLockup from '@/assets/logo-lockup.png';

export type NoticeTone = 'error' | 'warning' | 'success' | 'info';

const toneStyles: Record<NoticeTone, { box: string; icon: typeof Info }> = {
  error: { box: 'bg-danger-50 border-danger-500/30 text-danger-600', icon: XCircle },
  warning: {
    box: 'bg-warning-50 border-warning-500/40 text-warning-700',
    icon: AlertTriangle,
  },
  success: { box: 'bg-success-50 border-success-500/30 text-success-600', icon: CheckCircle2 },
  info: { box: 'bg-primary-50 border-primary-200 text-primary-700', icon: Info },
};

interface NoticeProps {
  tone: NoticeTone;
  title?: string;
  children: ReactNode;
}

/**
 * 로그인·회원가입 폼 안에 붙는 안내 배너.
 *
 * 승인 대기·정지 같은 403 사유는 토스트로 띄우면 3초 만에 사라져 왜 못 들어가는지
 * 알 수 없다. 폼 안에 남는 배너로 보여준다.
 */
export const AuthNotice = ({ tone, title, children }: NoticeProps) => {
  const { box, icon: Icon } = toneStyles[tone];
  return (
    <div
      role={tone === 'error' || tone === 'warning' ? 'alert' : 'status'}
      className={clsx('flex items-start gap-2.5 rounded-lg border px-3.5 py-3 text-sm', box)}
    >
      <Icon className='w-4 h-4 mt-0.5 shrink-0' aria-hidden='true' />
      <div className='min-w-0'>
        {title && <p className='font-semibold mb-0.5'>{title}</p>}
        <div className='leading-relaxed break-keep'>{children}</div>
      </div>
    </div>
  );
};

interface Props {
  title: string;
  description?: string;
  children: ReactNode;
  /** 카드 아래에 붙는 보조 링크 영역 */
  footer?: ReactNode;
  /** 입력이 많은 회원가입 폼용 넓은 카드 */
  wide?: boolean;
}

/** 로그인·회원가입이 공유하는 바깥 틀 */
export const AuthShell = ({ title, description, children, footer, wide = false }: Props) => (
  <div className='min-h-screen flex flex-col items-center justify-center bg-gray-100 px-4 py-10'>
    <div className={clsx('w-full', wide ? 'max-w-xl' : 'max-w-md')}>
      <div className='text-center mb-6'>
        <img
          src={logoLockup}
          alt={BRAND.NAME}
          className='mx-auto h-28 w-auto object-contain'
        />
        <p className='mt-3 text-sm font-semibold text-primary-700'>{BRAND.EXPANSION}</p>
        <p className='mt-1 text-[13px] text-gray-500'>{BRAND.MEANING}</p>
      </div>

      <div className='bg-white border border-gray-200 rounded-xl p-8'>
        <div className='mb-6 text-center'>
          <h1 className='text-lg font-bold text-gray-900'>{title}</h1>
          {description && (
            <p className='mt-1.5 text-[13px] text-gray-500 break-keep'>{description}</p>
          )}
        </div>

        {children}
      </div>

      {footer && <div className='mt-5 text-center text-sm text-gray-500'>{footer}</div>}
    </div>

    <p className='mt-6 max-w-lg text-center text-xs text-gray-400 leading-relaxed break-keep'>
      <span className='font-semibold text-gray-500'>{BRAND.ACRONYM}</span>
      {`는 ${BRAND.EXPANSION}의 약자로, ${BRAND.MEANING}를 뜻합니다.`}
      <br />
      {BRAND.DESCRIPTION}
    </p>
  </div>
);
